/* istanbul ignore file */
import { FieldErrors, FieldValues } from 'react-hook-form';
import { BlockType, IDIncomeVerificationStatus, VerificationFailureAction } from '@common/entities';
import { VerificationFieldState } from 'state/slices/idIncomeVerificationSlice';

type ErrorCountArrOfObj = { [key: string]: any }[] & { root?: unknown };
const getErrorCountFromArrOfObj = (arr: ErrorCountArrOfObj) => {
  if (!arr) return 0;

  let count = arr.root ? 1 : 0;
  arr.forEach((item) => {
    if (item) count += Object.keys(item).length;
  });
  return count;
};

type IncomeBlockErrors = {
  incomeTypes: ErrorCountArrOfObj;
  income_question: any;
  documentUploadMinValidation?: Record<string, unknown>;
};
const getIncomeBlockErrorCount = (errors: IncomeBlockErrors): number => {
  const { incomeTypes, income_question: incomeQuestion, documentUploadMinValidation } = errors;

  let count = 0;
  if (incomeQuestion) {
    count += 1;
  }

  if (incomeTypes && incomeTypes.length > 0) {
    count += getErrorCountFromArrOfObj(incomeTypes);
  } else if (!incomeQuestion && incomeTypes.length === 0) {
    count += 1;
  }

  count += Object.keys(documentUploadMinValidation ?? {}).length;

  return count;
};

type ExpenseBlockErrors = {
  expenseTypes: ErrorCountArrOfObj;
};
const getExpenseBlockErrorCount = (errors: ExpenseBlockErrors): number => {
  const { expenseTypes } = errors;

  let count = 0;
  if (expenseTypes && expenseTypes.length > 0) {
    count += getErrorCountFromArrOfObj(expenseTypes);
  }

  return count;
};

type AssetsBlockErrors = {
  assetTypes: ErrorCountArrOfObj;
  assets_question: any;
  documentUploadMinValidation?: Record<string, unknown>;
};
const getAssetsBlockErrorCount = (errors: AssetsBlockErrors): number => {
  const { assetTypes, assets_question: assetsQuestion, documentUploadMinValidation } = errors;

  let count = 0;
  if (assetsQuestion) {
    count += 1;
  }

  if (assetTypes && assetTypes.length > 0) {
    count += getErrorCountFromArrOfObj(assetTypes);
  } else if (!assetsQuestion && assetTypes.length === 0) {
    count += 1;
  }

  count += Object.keys(documentUploadMinValidation ?? {}).length;

  return count;
};

type PetBlockErrors = {
  petList: ErrorCountArrOfObj;
};
const getPetsBlockErrorCount = (errors: PetBlockErrors): number => {
  const { petList } = errors;
  let count = 0;

  if (petList) {
    count += getErrorCountFromArrOfObj(petList);
  }

  return count;
};

type CoApplicantBlockError = {
  coApplicantList: ErrorCountArrOfObj;
  coapplicant_count?: any;
};
const getCoApplicantBlockErrorCount = (errors: CoApplicantBlockError): number => {
  const { coApplicantList, coapplicant_count: coApplicantCount } = errors;

  let count = 0;
  if (coApplicantList) {
    count += getErrorCountFromArrOfObj(coApplicantList);
  }
  if (coApplicantCount) {
    count += 1;
  }

  return count;
};

type VehicleBlockError = {
  vehicleList: { [key: string]: any }[];
};
const getVehiclesBlockErrorCount = (errors: VehicleBlockError): number => {
  const { vehicleList } = errors;

  let count = 0;
  if (vehicleList) {
    count += getErrorCountFromArrOfObj(vehicleList);
  }
  return count;
};

type IdentificationErrorType = {
  additionalPrimaryId: ErrorCountArrOfObj;
  alternateId: ErrorCountArrOfObj;
  primaryId: ErrorCountArrOfObj;
};
const getIdentificationBlockErrorCount = (errors: IdentificationErrorType): number => {
  const { additionalPrimaryId, alternateId, primaryId } = errors;
  let count = 0;

  if (primaryId) {
    count += getErrorCountFromArrOfObj(primaryId);
  }
  if (additionalPrimaryId) {
    count += getErrorCountFromArrOfObj(additionalPrimaryId);
  }
  if (alternateId) {
    count += getErrorCountFromArrOfObj(alternateId);
  }
  return count;
};

type GuarantorBlockError = {
  guarantorList: ErrorCountArrOfObj;
  guarantor_count?: any;
};
const getGuarantorBlockErrorCount = (errors: GuarantorBlockError): number => {
  const { guarantorList, guarantor_count: guarantorCount } = errors;
  let count = 0;
  if (guarantorList) {
    count += getErrorCountFromArrOfObj(guarantorList);
  }
  if (guarantorCount) {
    count += 1;
  }

  return count;
};

const getAddressBlockErrorCount = (errors: ErrorCountArrOfObj): number => {
  let count = 0;
  if (errors) {
    count += getErrorCountFromArrOfObj(errors);
  }
  return count;
};

const getFlatBlockErrorCount = (errors: ErrorCountArrOfObj) => {
  let count = 0;

  if (errors) {
    count += Object.values(errors)?.length || 0;
  }

  return count;
};

const BLOCK_KEYS = {
  [BlockType.Asset]: getAssetsBlockErrorCount,
  coApplicant: getCoApplicantBlockErrorCount,
  guarantor: getGuarantorBlockErrorCount,
  [BlockType.IdentificationType]: getIdentificationBlockErrorCount,
  [BlockType.Income]: getIncomeBlockErrorCount,
  [BlockType.Pet]: getPetsBlockErrorCount,
  [BlockType.Vehicle]: getVehiclesBlockErrorCount,
  [BlockType.AddressCurrent]: getAddressBlockErrorCount,
  [BlockType.AddressPrevious]: getAddressBlockErrorCount,
  [BlockType.GeneralQuestion]: getFlatBlockErrorCount,
  [BlockType.EmergencyContact]: getFlatBlockErrorCount,
  [BlockType.Disability]: getFlatBlockErrorCount,
  [BlockType.MilitaryVeteran]: getFlatBlockErrorCount,
  [BlockType.Student]: getFlatBlockErrorCount,
  [BlockType.HousedDeclaredDisaster]: getFlatBlockErrorCount,
  [BlockType.HouseholdFleeingDomesticViolence]: getFlatBlockErrorCount,
  [BlockType.PoliceSecurityOfficer]: getFlatBlockErrorCount,
  [BlockType.Expense]: getExpenseBlockErrorCount,
  [BlockType.RoommateInterests]: getFlatBlockErrorCount,
};

type Keys = keyof typeof BLOCK_KEYS;

function getBlockFieldErrorCount(key: Keys, errors: any): number {
  const fn = BLOCK_KEYS[key];
  return fn(errors);
}

export function getErrorCount(errors: FieldErrors<FieldValues>) {
  if (!Object.keys(errors)) return 0;
  let count = 0;
  Object.keys(errors).forEach((error: string) => {
    if (typeof errors[error] === 'object' && Object.keys(BLOCK_KEYS).includes(error)) {
      count += getBlockFieldErrorCount(error as Keys, errors[error]);
      return;
    }
    count += 1;
  });
  return count;
}

export const verificationErrorCount = (data: VerificationFieldState) => {
  const { isRequired, status, verificationFailureAction } = data;

  if (status === IDIncomeVerificationStatus.Success) return 0;

  const isRestrictiveAction =
    verificationFailureAction === VerificationFailureAction.ApplicationCantBeCompleted ||
    verificationFailureAction === VerificationFailureAction.LockdownTheApplication;

  const isIncomplete =
    status === IDIncomeVerificationStatus.NotStarted ||
    status === IDIncomeVerificationStatus.Initiated ||
    status === IDIncomeVerificationStatus.ReInitiated;

  if (isRequired) {
    if (isIncomplete) return 1;
    if (verificationFailureAction === VerificationFailureAction.DoNothing) return 0;
    if (status === IDIncomeVerificationStatus.InProgress) return 1;
    if (status === IDIncomeVerificationStatus.Failed) return 1;
    return 0;
  }

  if (status === IDIncomeVerificationStatus.InProgress && isRestrictiveAction) return 1;
  if (status === IDIncomeVerificationStatus.Failed && isRestrictiveAction) return 1;

  return 0;
};
